declare global {
  interface Window {
    google?: any;
    googleTranslateElementInit?: () => void;
  }
}

export type TranslateVariant = 'desktop' | 'mobile';

const TRANSLATE_HOST_ID = 'google_translate_element';
const TRANSLATE_SCRIPT_SELECTOR = 'script[src*="translate.google.com"]';
const TRANSLATE_SCRIPT_URL = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
const SCRIPT_LOAD_TIMEOUT_MS = 15_000;
/** Matches `@include desktop` in navbar styles (`$bs-lg-min-width`). */
const DESKTOP_MEDIA_QUERY = '(min-width: 1025px)';

let translateHostEl: HTMLDivElement | null = null;
let translateInitialized = false;
let scriptLoading = false;
let mediaQueryList: MediaQueryList | null = null;

const mountContainers: Partial<Record<TranslateVariant, HTMLElement>> = {};

function findTranslateScript(): HTMLScriptElement | null {
  return document.head.querySelector<HTMLScriptElement>(TRANSLATE_SCRIPT_SELECTOR);
}

function removeTranslateScripts(): void {
  document.head.querySelectorAll(TRANSLATE_SCRIPT_SELECTOR).forEach((script) => script.remove());
}

function isTranslateApiReady(): boolean {
  return Boolean(window.google?.translate?.TranslateElement);
}

function isDesktopLayout(): boolean {
  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
}

function getActiveVariant(): TranslateVariant {
  return isDesktopLayout() ? 'desktop' : 'mobile';
}

function getOrCreateTranslateHost(): HTMLDivElement {
  if (!translateHostEl) {
    translateHostEl = document.createElement('div');
    translateHostEl.id = TRANSLATE_HOST_ID;
  }
  return translateHostEl;
}

function attachHostToActiveVariant(): void {
  const host = getOrCreateTranslateHost();
  const container = mountContainers[getActiveVariant()];
  if (!container || container.contains(host)) {
    return;
  }
  container.appendChild(host);
}

function handleLayoutChange(): void {
  attachHostToActiveVariant();
}

function subscribeToLayoutChange(listener: () => void): void {
  if (!mediaQueryList) {
    return;
  }
  if (typeof mediaQueryList.addEventListener === 'function') {
    mediaQueryList.addEventListener('change', listener);
    return;
  }
  mediaQueryList.addListener(listener);
}

function unsubscribeFromLayoutChange(listener: () => void): void {
  if (!mediaQueryList) {
    return;
  }
  if (typeof mediaQueryList.removeEventListener === 'function') {
    mediaQueryList.removeEventListener('change', listener);
    return;
  }
  mediaQueryList.removeListener(listener);
}

function ensureLayoutListener(): void {
  if (mediaQueryList || typeof window.matchMedia !== 'function') {
    return;
  }
  mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY);
  subscribeToLayoutChange(handleLayoutChange);
}

function resetLoadFailure(script: HTMLScriptElement | null, timeoutId?: number): void {
  if (timeoutId !== undefined) {
    window.clearTimeout(timeoutId);
  }
  scriptLoading = false;
  delete window.googleTranslateElementInit;
  script?.remove();
}

function patchTranslateAnchors(host: HTMLElement): void {
  const patchAnchors = (root: Element) => {
    root.querySelectorAll<HTMLAnchorElement>('a[href="#"]').forEach((anchor) => {
      anchor.href = 'javascript:void(0)'; // eslint-disable-line no-script-url
    });
  };

  patchAnchors(host);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          patchAnchors(node as Element);
        }
      });
    });
  });
  observer.observe(host, { childList: true, subtree: true });
}

function initTranslateWidget(): void {
  if (translateInitialized || !isTranslateApiReady()) {
    return;
  }

  const host = getOrCreateTranslateHost();
  if (host.hasChildNodes()) {
    translateInitialized = true;
    return;
  }

  try {
    new window.google.translate.TranslateElement(
      {
        pageLanguage: 'en',
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        autoDisplay: false,
        multilanguagePage: false,
        gaTrack: false,
        gaId: null,
        floatPosition: window.google.translate.TranslateElement.FloatPosition.TOP_LEFT,
      },
      host.id,
    );
    translateInitialized = true;

    setTimeout(() => patchTranslateAnchors(host), 500);

    setTimeout(() => {
      if (window.location.hash.includes('googtrans') || window.location.hash === '#') {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }, 100);
  } catch (err) {
    console.error('[GoogleTranslateWidget] Initialization error:', err);
  }
}

function finishScriptLoad(): void {
  initTranslateWidget();
  attachHostToActiveVariant();
}

function watchExistingScript(existing: HTMLScriptElement): void {
  scriptLoading = true;

  const timeoutId = window.setTimeout(() => {
    if (!isTranslateApiReady()) {
      resetLoadFailure(existing, timeoutId);
      console.error('[GoogleTranslateWidget] Google Translate script load timed out');
    }
  }, SCRIPT_LOAD_TIMEOUT_MS);

  existing.addEventListener(
    'load',
    () => {
      window.clearTimeout(timeoutId);
      finishScriptLoad();
    },
    { once: true },
  );

  existing.addEventListener(
    'error',
    () => {
      resetLoadFailure(existing, timeoutId);
      console.error('[GoogleTranslateWidget] Failed to load Google Translate script');
    },
    { once: true },
  );

  setTimeout(finishScriptLoad, 200);
}

function injectScript(): void {
  scriptLoading = true;

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = TRANSLATE_SCRIPT_URL;
  script.async = true;

  const timeoutId = window.setTimeout(() => {
    if (!isTranslateApiReady()) {
      resetLoadFailure(script, timeoutId);
      console.error('[GoogleTranslateWidget] Google Translate script load timed out');
    }
  }, SCRIPT_LOAD_TIMEOUT_MS);

  const onScriptReady = () => {
    window.clearTimeout(timeoutId);
    finishScriptLoad();
  };

  window.googleTranslateElementInit = () => {
    setTimeout(onScriptReady, 100);
  };

  script.onload = onScriptReady;
  script.onerror = () => {
    resetLoadFailure(script, timeoutId);
    console.error('[GoogleTranslateWidget] Failed to load Google Translate script');
  };

  document.head.appendChild(script);
}

export function registerVariantMount(variant: TranslateVariant, container: HTMLElement): void {
  mountContainers[variant] = container;
  ensureLayoutListener();
  attachHostToActiveVariant();
  loadGoogleTranslateScript();
}

export function unregisterVariantMount(variant: TranslateVariant, container: HTMLElement): void {
  if (mountContainers[variant] === container) {
    delete mountContainers[variant];
  }

  const host = translateHostEl;
  if (host && container.contains(host)) {
    container.removeChild(host);
  }

  attachHostToActiveVariant();
}

export function loadGoogleTranslateScript(): void {
  if (isTranslateApiReady()) {
    finishScriptLoad();
    return;
  }

  if (scriptLoading) {
    return;
  }

  const existing = findTranslateScript();
  if (existing) {
    watchExistingScript(existing);
    return;
  }

  injectScript();
}

/** Returns the singleton translate host element. For unit tests only. */
export function getTranslateHostForTests(): HTMLDivElement {
  return getOrCreateTranslateHost();
}

/** Clears module state between unit tests. Not for production use. */
export function resetGoogleTranslateLoaderForTests(): void {
  if (mediaQueryList) {
    unsubscribeFromLayoutChange(handleLayoutChange);
    mediaQueryList = null;
  }

  scriptLoading = false;
  translateInitialized = false;
  delete window.googleTranslateElementInit;
  removeTranslateScripts();

  if (translateHostEl?.parentNode) {
    translateHostEl.parentNode.removeChild(translateHostEl);
  }
  translateHostEl = null;

  delete mountContainers.desktop;
  delete mountContainers.mobile;
  delete window.google;
}

export { TRANSLATE_HOST_ID };
